'use client';

import Link from 'next/link';
import Image from 'next/image';
import { MarketingNav } from '@/components/marketing/MarketingNav';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';

export default function HomePage() {
  return (
    <>
      <MarketingNav />

      <main className="pt-[56px]">
        {/* ── Hero ──────────────────────────────────────────────── */}
        <section className="bg-white">
          <div className="max-w-[1200px] mx-auto px-6 py-20 lg:py-28">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h1 className="text-[36px] sm:text-[44px] lg:text-[52px] font-semibold text-[#1a1a1a] leading-[1.1] tracking-[-0.02em] mb-6">
                  Professional email for teams and individuals
                </h1>
                <p className="text-[18px] text-[#555] leading-[1.65] mb-8 max-w-[480px]">
                  TekSpert Mail is a secure email hosting platform with native desktop apps, custom domain support, and enterprise-grade encryption — built for businesses and individuals who need reliable, private email.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    href="/auth/register"
                    className="inline-flex items-center px-6 py-3 bg-[#0078D4] hover:bg-[#005A9E] text-white text-[15px] font-semibold rounded-[4px] transition-colors"
                  >
                    Create a free account
                  </Link>
                  <Link
                    href="/download"
                    className="inline-flex items-center px-6 py-3 bg-white hover:bg-[#f5f5f5] text-[#333] text-[15px] font-semibold rounded-[4px] border border-[#d2d2d2] transition-colors"
                  >
                    Download desktop app
                  </Link>
                </div>
              </div>

              <div className="relative">
                <Image
                  src="/images/hero-mockup.png"
                  alt="TekSpert Mail email client showing inbox with three-column layout"
                  width={700}
                  height={525}
                  className="w-full h-auto rounded-[8px] shadow-[0_4px_24px_rgba(0,0,0,0.08)] border border-[#e5e5e5]"
                  priority
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── What you get ─────────────────────────────────────── */}
        <section className="bg-[#fafafa] border-t border-[#eee]">
          <div className="max-w-[1200px] mx-auto px-6 py-20 lg:py-24">
            <div className="max-w-[640px] mb-14">
              <h2 className="text-[28px] sm:text-[32px] font-semibold text-[#1a1a1a] leading-tight mb-4">
                A complete email platform
              </h2>
              <p className="text-[16px] text-[#555] leading-[1.65]">
                Everything you need to manage your email — from a powerful web client to native desktop applications for Windows and macOS.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: 'Multi-account inbox',
                  text: 'Manage multiple email addresses — whether on the tekspertmail.co.uk domain or your own custom domain — from a single, unified interface. Switch between accounts instantly or view everything at once.',
                },
                {
                  title: 'Desktop applications',
                  text: 'Native apps for Windows and macOS provide faster performance, system notifications, and the ability to work with your email even when you have limited connectivity.',
                },
                {
                  title: 'End-to-end encryption',
                  text: 'Every email is encrypted in transit and at rest using AES-256 encryption. Your messages are protected from the moment you hit send until they reach the recipient.',
                },
                {
                  title: 'Rich email composer',
                  text: 'Write professional emails with a full-featured rich text editor, HTML signature support, file attachments, and the ability to schedule messages to send later.',
                },
                {
                  title: 'Fast, reliable sync',
                  text: 'Our sync engine checks for new mail in real-time so your inbox is always up to date. Search across thousands of emails with results in under 100 milliseconds.',
                },
                {
                  title: 'No ads, no tracking',
                  text: 'We do not display advertisements, track your email activity, or sell your data to third parties. Your email is private — that is a principle we will never compromise on.',
                },
              ].map((item) => (
                <div key={item.title} className="py-2">
                  <h3 className="text-[17px] font-semibold text-[#1a1a1a] mb-2">{item.title}</h3>
                  <p className="text-[15px] text-[#555] leading-[1.65]">{item.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-12">
              <Link href="/features" className="text-[15px] font-semibold text-[#0078D4] hover:underline">
                See all features →
              </Link>
            </div>
          </div>
        </section>

        {/* ── Security summary ─────────────────────────────────── */}
        <section className="bg-white border-t border-[#eee]">
          <div className="max-w-[1200px] mx-auto px-6 py-20 lg:py-24">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
              <div>
                <h2 className="text-[28px] sm:text-[32px] font-semibold text-[#1a1a1a] leading-tight mb-4">
                  Built with security at every layer
                </h2>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-6">
                  TekSpert Mail was designed from the ground up to keep your communications private. We use industry-standard encryption protocols and follow strict data handling practices to ensure your emails are never exposed to unauthorised access.
                </p>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-8">
                  All data is encrypted in transit using TLS 1.3 and at rest using AES-256. We are fully GDPR compliant and do not process, scan, or monetise your email content in any way.
                </p>
                <Link href="/security" className="text-[15px] font-semibold text-[#0078D4] hover:underline">
                  Learn more about our security →
                </Link>
              </div>

              <div className="space-y-4">
                {[
                  { label: 'AES-256 encryption', detail: 'All stored email data is encrypted using the Advanced Encryption Standard with 256-bit keys.' },
                  { label: 'TLS 1.3 in transit', detail: 'Connections between your device and our servers are secured with the latest TLS protocol.' },
                  { label: 'GDPR compliant', detail: 'We comply fully with EU General Data Protection Regulation requirements for data handling and user rights.' },
                  { label: 'No data monetisation', detail: 'We do not scan, analyse, or sell your email content. Our revenue comes from subscriptions, not advertising.' },
                ].map((item) => (
                  <div key={item.label} className="p-4 bg-[#fafafa] rounded-[6px] border border-[#eee]">
                    <div className="text-[15px] font-semibold text-[#1a1a1a] mb-1">{item.label}</div>
                    <div className="text-[14px] text-[#666] leading-[1.55]">{item.detail}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Download ─────────────────────────────────────────── */}
        <section className="bg-[#fafafa] border-t border-[#eee]">
          <div className="max-w-[800px] mx-auto px-6 py-20 lg:py-24 text-center">
            <h2 className="text-[28px] sm:text-[32px] font-semibold text-[#1a1a1a] leading-tight mb-4">
              Available on web, macOS, and Windows
            </h2>
            <p className="text-[16px] text-[#555] leading-[1.65] mb-10 max-w-[520px] mx-auto">
              Use TekSpert Mail directly in your browser or download the native desktop app for a faster, more integrated experience.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/downloads/TekSpert-Mail-1.0.0-arm64.dmg"
                className="flex items-center gap-3 px-6 py-3 bg-[#1a1a1a] text-white rounded-[4px] font-semibold text-[14px] hover:bg-[#333] transition-colors min-w-[200px] justify-center"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                Download for macOS
              </a>
              <a
                href="/downloads/TekSpert-Mail-Setup-1.0.0.exe"
                className="flex items-center gap-3 px-6 py-3 bg-[#0078D4] text-white rounded-[4px] font-semibold text-[14px] hover:bg-[#005A9E] transition-colors min-w-[200px] justify-center"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
                </svg>
                Download for Windows
              </a>
            </div>

            <p className="mt-5 text-[13px] text-[#999]">
              Version 1.0.0 · macOS 10.15 or later · Windows 10 or later (64-bit)
            </p>
          </div>
        </section>

        {/* ── CTA ──────────────────────────────────────────────── */}
        <section className="bg-[#0078D4] border-t border-[#006ABF]">
          <div className="max-w-[800px] mx-auto px-6 py-16 lg:py-20 text-center">
            <h2 className="text-[28px] sm:text-[32px] font-semibold text-white leading-tight mb-4">
              Get started with TekSpert Mail
            </h2>
            <p className="text-[16px] text-white/80 leading-[1.65] mb-8 max-w-[480px] mx-auto">
              Create your free account in under a minute. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/auth/register"
                className="px-6 py-3 bg-white text-[#0078D4] text-[15px] font-semibold rounded-[4px] hover:bg-[#f5f5f5] transition-colors"
              >
                Create free account
              </Link>
              <Link
                href="/auth/login"
                className="px-6 py-3 text-white text-[15px] font-semibold rounded-[4px] border border-white/40 hover:bg-white/10 transition-colors"
              >
                Sign in
              </Link>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </>
  );
}
